import { useRef, useState, useEffect } from "react";
import { UploadCloud, FileText, Loader2, Trash2, Save, Sparkles, CheckCircle2, RotateCcw, ImagePlus, X, ImageIcon, Layers } from "lucide-react";
import { api, formatApiErrorDetail, formatPrice } from "@/lib/api";
import { ALL_CATEGORIES, catLabel } from "@/lib/taxonomy";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const STAGES = [
  "Extrayendo texto de las páginas...",
  "Detectando marcas y SKU...",
  "Extrayendo precios y fotos con IA...",
  "Comparando con tu catálogo actual...",
];

const PETS = ["todos", "perro", "gato", "conejos-roedores", "aves", "peces"];
const norm = (s) => (s || "").toString().trim().toLowerCase();

const STATUS_META = {
  new: { label: "Nuevo", cls: "bg-teal/30 text-charcoal border-teal/50" },
  changed: { label: "Con cambios", cls: "bg-peach text-coral-dark border-coral/40" },
  unchanged: { label: "Sin cambios", cls: "bg-cream-2 text-muted-moss border-[#E3E7E2]" },
};

export const PdfUploader = ({ onSaved }) => {
  const inputRef = useRef();
  const [mode, setMode] = useState("pdf");
  const [file, setFile] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [stage, setStage] = useState(0);
  const [rows, setRows] = useState(null);
  const [extractedImages, setExtractedImages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [picker, setPicker] = useState(null); // row index for image picker
  const [groupParent, setGroupParent] = useState("");

  useEffect(() => {
    if (!parsing) return;
    setStage(0);
    const id = setInterval(() => setStage((s) => (s < STAGES.length - 1 ? s + 1 : s)), 1400);
    return () => clearInterval(id);
  }, [parsing]);

  const switchMode = (m) => { setMode(m); setFile(null); setImageFiles([]); setRows(null); };

  const pick = (fileList) => {
    const arr = Array.from(fileList || []);
    if (!arr.length) return;
    if (mode === "pdf") {
      const f = arr[0];
      if (!f.name.toLowerCase().endsWith(".pdf")) { toast.error("El archivo debe ser un PDF"); return; }
      setFile(f); setRows(null);
    } else {
      const imgs = arr.filter((f) => /\.(png|jpe?g|webp)$/i.test(f.name) || (f.type || "").startsWith("image/"));
      if (!imgs.length) { toast.error("Sube imágenes JPG, PNG o WEBP"); return; }
      setImageFiles((prev) => [...prev, ...imgs]); setRows(null);
    }
  };

  const removeImageFile = (i) => setImageFiles((prev) => prev.filter((_, idx) => idx !== i));

  const parse = async () => {
    setParsing(true);
    setRows(null);
    try {
      let data;
      if (mode === "pdf") {
        if (!file) { setParsing(false); return; }
        const fd = new FormData();
        fd.append("file", file);
        ({ data } = await api.post("/admin/parse-pdf", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 }));
      } else {
        if (!imageFiles.length) { setParsing(false); return; }
        const fd = new FormData();
        imageFiles.forEach((f) => fd.append("files", f));
        ({ data } = await api.post("/admin/parse-images", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 240000 }));
      }
      const images = data.images || [];
      setExtractedImages(images);

      // fetch current catalog (incl. pending) to diff by SKU
      let existing = [];
      try { existing = (await api.get("/products", { params: { include_all: true } })).data || []; } catch { existing = []; }
      const bySku = new Map();
      existing.forEach((p) => { if (norm(p.sku)) bySku.set(norm(p.sku), p); });

      const parsed = (data.products || []).map((p, i) => {
        const base = { _include: true, _group: false, sku: "", pet_type: "todos", presentation: "", description: "", image_url: "", variants: [], ...p };
        base.image_url = p.image_url || images[i] || "";
        const match = norm(base.sku) ? bySku.get(norm(base.sku)) : null;
        if (!match) {
          return { ...base, _status: "new", _include: true };
        }
        const priceChanged = Number(match.price) !== Number(base.price);
        const descChanged = norm(match.description) !== norm(base.description);
        const changed = priceChanged || descChanged;
        return {
          ...base,
          _status: changed ? "changed" : "unchanged",
          _include: changed,
          _existing: match,
          _oldPrice: match.price,
          _oldDescription: match.description || "",
          _priceChanged: priceChanged,
          _descChanged: descChanged,
        };
      });
      setRows(parsed);
      const nNew = parsed.filter((r) => r._status === "new").length;
      const nChg = parsed.filter((r) => r._status === "changed").length;
      toast.success(`Se detectaron ${data.count} productos · ${nNew} nuevos, ${nChg} con cambios`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo procesar el contenido");
    } finally {
      setParsing(false);
    }
  };

  const updateRow = (i, key, value) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [key]: value } : row)));
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));

  const mkVar = (r) => (r.variants && r.variants.length)
    ? r.variants.map((v) => ({ name: v.name, sku: v.sku || "", price: Number(v.price) || 0 }))
    : [{ name: r.presentation || r.name || "Único", sku: r.sku || "", price: Number(r.price) || 0 }];

  const groupSelected = () => {
    const selIdx = rows.map((r, i) => (r._group ? i : -1)).filter((i) => i >= 0);
    if (selIdx.length < 2) { toast.error("Marca al menos 2 productos para agrupar"); return; }
    const pIdx = groupParent !== "" && selIdx.includes(Number(groupParent)) ? Number(groupParent) : selIdx[0];
    const parent = rows[pIdx];
    let variants = mkVar(parent);
    selIdx.forEach((i) => { if (i !== pIdx) variants = variants.concat(mkVar(rows[i])); });
    const seen = new Set();
    const deduped = [];
    variants.forEach((v) => { const k = `${v.sku || ""}|${v.name || ""}`; if (!seen.has(k)) { seen.add(k); deduped.push(v); } });
    const newParent = { ...parent, variants: deduped, presentation: "", sku: "", price: deduped[0]?.price ?? parent.price, _group: false };
    const childSet = new Set(selIdx.filter((i) => i !== pIdx));
    setRows((prev) => prev.map((r, i) => (i === pIdx ? newParent : r)).filter((_, i) => !childSet.has(i)));
    setGroupParent("");
    toast.success(`Agrupados ${selIdx.length} productos en "${parent.name}" (${deduped.length} variantes)`);
  };

  const clearGroup = () => setRows((r) => r.map((row) => ({ ...row, _group: false })));

  const save = async () => {
    if (!rows || rows.length === 0) return;
    const included = rows.filter((r) => r._include);
    if (included.length === 0) { toast.error("Marca al menos un producto para importar o actualizar"); return; }
    setSaving(true);
    try {
      const creates = included.filter((r) => r._status === "new");
      const updates = included.filter((r) => r._status !== "new" && r._existing);

      let inserted = 0;
      if (creates.length) {
        const products = creates.map((r) => ({
          name: r.name, price: Number(r.price) || 0, currency: "₡", sku: r.sku || "",
          pet_type: r.pet_type || "todos", category: r.category, presentation: r.presentation || "",
          description: r.description || "", image_url: r.image_url || "",
          images: r.image_url ? [r.image_url] : [], variants: r.variants || [], in_stock: true, featured: false,
        }));
        const { data } = await api.post("/products/bulk", { products, replace: false });
        inserted = data.inserted;
      }

      let updated = 0;
      for (const r of updates) {
        const ex = r._existing;
        const payload = {
          name: r.name, price: Number(r.price) || 0, currency: ex.currency || "₡",
          description: r.description || "", category: r.category, pet_type: r.pet_type || "todos",
          image_url: r.image_url || ex.image_url || "",
          images: r.image_url ? Array.from(new Set([r.image_url, ...(ex.images || [])])) : (ex.images || []),
          presentation: r.presentation || "", size: ex.size || "", sku: r.sku || "",
          discount: ex.discount || 0, variants: ex.variants || [],
          in_stock: ex.in_stock !== false, featured: !!ex.featured,
        };
        await api.put(`/products/${ex.id}`, payload);
        updated += 1;
      }

      const parts = [];
      if (inserted) parts.push(`${inserted} nuevo(s)`);
      if (updated) parts.push(`${updated} actualizado(s)`);
      toast.success(`Catálogo guardado: ${parts.join(" · ") || "sin cambios"}`);
      setRows(null); setFile(null); setImageFiles([]); setExtractedImages([]);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const allIncluded = rows && rows.length > 0 && rows.every((r) => r._include);
  const toggleAll = () => setRows((r) => r.map((row) => ({ ...row, _include: !allIncluded })));
  const includedCount = rows ? rows.filter((r) => r._include).length : 0;
  const groupSelIdx = rows ? rows.map((r, i) => (r._group ? i : -1)).filter((i) => i >= 0) : [];

  return (
    <div className="space-y-5">
      {!rows && (
        <>
          <div className="flex gap-2">
            <button data-testid="mode-pdf" onClick={() => switchMode("pdf")} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-2xl text-sm font-semibold border transition-all ${mode === "pdf" ? "bg-coral text-white border-coral" : "bg-white text-charcoal border-[#E3E7E2] hover:bg-peach/50"}`}>
              <FileText className="w-4 h-4" /> Desde PDF
            </button>
            <button data-testid="mode-images" onClick={() => switchMode("images")} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-2xl text-sm font-semibold border transition-all ${mode === "images" ? "bg-coral text-white border-coral" : "bg-white text-charcoal border-[#E3E7E2] hover:bg-peach/50"}`}>
              <ImagePlus className="w-4 h-4" /> Desde imágenes
            </button>
          </div>

          <div
            data-testid="pdf-upload-dropzone"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files); }}
            className="border-2 border-dashed border-teal/70 rounded-3xl bg-white p-8 sm:p-12 text-center cursor-pointer hover:bg-sage/20 transition-colors"
          >
            {mode === "pdf" ? (
              <>
                <input ref={inputRef} data-testid="pdf-file-input" type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => pick(e.target.files)} />
                <span className="grid place-items-center w-16 h-16 mx-auto rounded-2xl bg-peach text-coral mb-4">
                  {file ? <FileText className="w-8 h-8" /> : <UploadCloud className="w-8 h-8" />}
                </span>
                {file ? (
                  <div>
                    <p className="font-semibold text-charcoal">{file.name}</p>
                    <p className="text-sm text-muted-moss mt-1">{(file.size / 1024).toFixed(0)} KB · Listo para procesar</p>
                  </div>
                ) : (
                  <div>
                    <p className="font-display text-lg font-bold text-charcoal">Arrastra tu catálogo PDF aquí</p>
                    <p className="text-sm text-muted-moss mt-1">o toca para seleccionar el archivo</p>
                  </div>
                )}
              </>
            ) : (
              <>
                <input ref={inputRef} data-testid="images-file-input" type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" onChange={(e) => pick(e.target.files)} />
                <span className="grid place-items-center w-16 h-16 mx-auto rounded-2xl bg-peach text-coral mb-4"><ImagePlus className="w-8 h-8" /></span>
                <p className="font-display text-lg font-bold text-charcoal">Arrastra fotos de productos aquí</p>
                <p className="text-sm text-muted-moss mt-1">JPG, PNG o WEBP · una foto por producto (o etiqueta)</p>
              </>
            )}
          </div>

          {mode === "images" && imageFiles.length > 0 && (
            <div data-testid="selected-images-preview" className="flex gap-2 flex-wrap">
              {imageFiles.map((f, i) => (
                <div key={i} className="relative w-20 h-20 rounded-xl overflow-hidden border border-[#E3E7E2] bg-cream-2">
                  <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                  <button onClick={(e) => { e.stopPropagation(); removeImageFile(i); }} className="absolute top-0.5 right-0.5 grid place-items-center w-5 h-5 rounded-full bg-white/90 text-destructive"><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}

          <button data-testid="start-pdf-parse-button" onClick={parse} disabled={parsing || (mode === "pdf" ? !file : imageFiles.length === 0)} className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl bg-coral text-white font-bold hover:bg-coral-dark active:scale-95 transition-all disabled:opacity-40 card-shadow">
            {parsing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
            {parsing ? "Procesando con IA..." : (mode === "pdf" ? "Cargar catálogo con IA" : `Procesar ${imageFiles.length || ""} imagen(es) con IA`)}
          </button>

          {parsing && (
            <div data-testid="pdf-parse-progress-indicator" className="bg-white rounded-2xl border border-[#E3E7E2] p-5 space-y-3">
              {STAGES.map((s, i) => (
                <div key={i} className={`flex items-center gap-3 text-sm transition-opacity ${i <= stage ? "opacity-100" : "opacity-40"}`}>
                  {i < stage ? <CheckCircle2 className="w-5 h-5 text-teal shrink-0" /> : i === stage ? <Loader2 className="w-5 h-5 text-coral animate-spin shrink-0" /> : <span className="w-5 h-5 rounded-full border-2 border-[#E3E7E2] shrink-0" />}
                  <span className={i <= stage ? "text-charcoal font-medium" : "text-muted-moss"}>{s}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {rows && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div>
              <h3 className="font-display text-xl font-bold text-charcoal">Revisa y verifica ({includedCount}/{rows.length})</h3>
              <p className="text-sm text-muted-moss">Verifica cada columna, revisa los cambios en amarillo y asigna una foto a cada producto.</p>
            </div>
            <button onClick={() => { setRows(null); setFile(null); setImageFiles([]); setExtractedImages([]); }} className="inline-flex items-center gap-1.5 text-sm text-muted-moss hover:text-coral">
              <RotateCcw className="w-4 h-4" /> Empezar de nuevo
            </button>
          </div>

          {extractedImages.length > 0 && (
            <div className="rounded-2xl border border-[#E3E7E2] bg-white p-3">
              <p className="text-xs font-semibold text-muted-moss mb-2">Fotos disponibles ({extractedImages.length}) · asignadas automáticamente por orden, ajústalas si es necesario</p>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {extractedImages.map((u, i) => (
                  <img key={i} src={u} alt="" className="w-14 h-14 rounded-lg object-cover border border-[#E3E7E2] shrink-0" />
                ))}
              </div>
            </div>
          )}

          {groupSelIdx.length >= 2 && (
            <div data-testid="group-toolbar" className="rounded-2xl border border-coral/40 bg-peach/60 p-3 flex flex-wrap items-center gap-3">
              <span className="text-sm font-bold text-charcoal">{groupSelIdx.length} seleccionados para agrupar</span>
              <span className="text-xs text-muted-moss">Producto madre:</span>
              <select data-testid="group-parent-select" value={groupParent} onChange={(e) => setGroupParent(e.target.value)} className="px-2 py-1.5 rounded-lg border border-[#E3E7E2] bg-white text-sm outline-none focus:border-coral max-w-[240px]">
                <option value="">{rows[groupSelIdx[0]]?.name?.slice(0, 40)} (primero)</option>
                {groupSelIdx.map((i) => (<option key={i} value={i}>{rows[i].name?.slice(0, 40)}</option>))}
              </select>
              <button data-testid="group-as-variants" onClick={groupSelected} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-coral text-white text-sm font-bold hover:bg-coral-dark active:scale-95 transition-all">
                <Layers className="w-4 h-4" /> Agrupar como variantes
              </button>
              <button data-testid="group-clear" onClick={clearGroup} className="text-sm text-muted-moss hover:text-charcoal">Cancelar</button>
            </div>
          )}

          <div className="overflow-x-auto rounded-2xl border border-[#E3E7E2] bg-white">
            <table data-testid="parsed-products-table" className="w-full text-sm min-w-[1120px]">
              <thead className="bg-cream-2 text-left text-muted-moss">
                <tr>
                  <th className="p-3"><input data-testid="parsed-select-all" type="checkbox" checked={!!allIncluded} onChange={toggleAll} className="w-4 h-4 accent-[#E08A7E]" /></th>
                  <th className="p-3 font-semibold" title="Marca para agrupar como variantes"><Layers className="w-4 h-4" /></th>
                  <th className="p-3 font-semibold">Estado</th>
                  <th className="p-3 font-semibold">Foto</th>
                  <th className="p-3 font-semibold">Producto</th>
                  <th className="p-3 font-semibold">SKU</th>
                  <th className="p-3 font-semibold">Mascota</th>
                  <th className="p-3 font-semibold">Categoría</th>
                  <th className="p-3 font-semibold">Presentación</th>
                  <th className="p-3 font-semibold">Descripción</th>
                  <th className="p-3 font-semibold">Precio</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const meta = STATUS_META[r._status] || STATUS_META.new;
                  return (
                    <tr key={i} className={`border-t border-[#E3E7E2] align-top ${r._include ? "" : "opacity-45"}`}>
                      <td className="p-2 text-center pt-3">
                        <input data-testid={`parsed-include-${i}`} type="checkbox" checked={!!r._include} onChange={(e) => updateRow(i, "_include", e.target.checked)} className="w-4 h-4 accent-[#E08A7E]" />
                      </td>
                      <td className="p-2 text-center pt-3">
                        <input data-testid={`group-select-${i}`} type="checkbox" checked={!!r._group} onChange={(e) => updateRow(i, "_group", e.target.checked)} className="w-4 h-4 accent-[#E08A7E]" />
                      </td>
                      <td className="p-2 pt-3">
                        <span data-testid={`parsed-status-${i}`} className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border ${meta.cls}`}>{meta.label}</span>
                      </td>
                      <td className="p-2">
                        <button data-testid={`parsed-photo-${i}`} onClick={() => setPicker(i)} className="relative w-14 h-14 rounded-lg overflow-hidden border border-[#E3E7E2] bg-cream-2 grid place-items-center hover:border-coral">
                          {r.image_url ? <img src={r.image_url} alt="" className="w-full h-full object-cover" /> : <ImagePlus className="w-5 h-5 text-muted-moss" />}
                        </button>
                      </td>
                      <td className="p-2">
                        <input value={r.name} onChange={(e) => updateRow(i, "name", e.target.value)} className="w-full min-w-[150px] px-2 py-1.5 rounded-lg border border-[#E3E7E2] bg-cream outline-none focus:border-coral" />
                        {r.variants && r.variants.length > 0 && (
                          <div data-testid={`parsed-variants-${i}`} className="flex flex-wrap gap-1 mt-1.5">
                            {r.variants.map((v, vi) => (
                              <span key={vi} className="inline-flex items-center gap-1 text-[10px] font-semibold bg-sage/50 text-charcoal rounded-full px-2 py-0.5">
                                {v.name || "—"}{v.sku ? ` · ${v.sku}` : ""} · {formatPrice(v.price)}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="p-2">
                        {r.variants && r.variants.length > 0 ? (
                          <span className="text-xs text-muted-moss">{r.variants.length} var.</span>
                        ) : (
                          <input value={r.sku || ""} onChange={(e) => updateRow(i, "sku", e.target.value)} className="w-24 px-2 py-1.5 rounded-lg border border-[#E3E7E2] bg-cream outline-none focus:border-coral" placeholder="—" />
                        )}
                      </td>
                      <td className="p-2">
                        <select value={r.pet_type || "todos"} onChange={(e) => updateRow(i, "pet_type", e.target.value)} className="px-2 py-1.5 rounded-lg border border-[#E3E7E2] bg-cream outline-none focus:border-coral">
                          {PETS.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </td>
                      <td className="p-2">
                        <select value={r.category} onChange={(e) => updateRow(i, "category", e.target.value)} className="px-2 py-1.5 rounded-lg border border-[#E3E7E2] bg-cream outline-none focus:border-coral">
                          {ALL_CATEGORIES.map((c) => <option key={c} value={c}>{catLabel(c)}</option>)}
                        </select>
                      </td>
                      <td className="p-2"><input value={r.presentation || ""} onChange={(e) => updateRow(i, "presentation", e.target.value)} className="w-full min-w-[90px] px-2 py-1.5 rounded-lg border border-[#E3E7E2] bg-cream outline-none focus:border-coral" /></td>
                      <td className="p-2">
                        <input value={r.description || ""} onChange={(e) => updateRow(i, "description", e.target.value)} className="w-full min-w-[160px] px-2 py-1.5 rounded-lg border border-[#E3E7E2] bg-cream outline-none focus:border-coral" placeholder="—" />
                        {r._status === "changed" && r._descChanged && (
                          <p className="text-[11px] text-muted-moss mt-1">antes: <span className="line-through">{r._oldDescription || "—"}</span></p>
                        )}
                      </td>
                      <td className="p-2">
                        <input data-testid={`edit-parsed-price-${i}`} type="number" value={r.price} onChange={(e) => updateRow(i, "price", e.target.value)} className="w-24 px-2 py-1.5 rounded-lg border border-[#E3E7E2] bg-cream outline-none focus:border-coral font-semibold text-coral" />
                        {r.variants && r.variants.length > 0 && (
                          <p className="text-[10px] text-muted-moss mt-1">precio base (desde)</p>
                        )}
                        {r._status === "changed" && r._priceChanged && (
                          <p className="text-[11px] text-muted-moss mt-1">antes: <span className="line-through">{formatPrice(r._oldPrice)}</span></p>
                        )}
                      </td>
                      <td className="p-2"><button onClick={() => removeRow(i)} className="grid place-items-center w-8 h-8 rounded-lg text-muted-moss hover:text-destructive hover:bg-destructive/10"><Trash2 className="w-4 h-4" /></button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <button data-testid="save-parsed-catalog-button" onClick={save} disabled={saving} className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl bg-coral text-white font-bold hover:bg-coral-dark active:scale-95 transition-all disabled:opacity-60 card-shadow">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            Aplicar cambios ({includedCount})
          </button>
        </div>
      )}

      <Dialog open={picker !== null} onOpenChange={(o) => !o && setPicker(null)}>
        <DialogContent className="max-w-lg rounded-3xl bg-cream border-[#E3E7E2] max-h-[80vh] overflow-y-auto">
          <DialogTitle className="font-display text-lg font-bold text-charcoal">Asignar foto del PDF</DialogTitle>
          <DialogDescription className="text-sm text-muted-moss">Elige la foto extraída que corresponde a este producto.</DialogDescription>
          {extractedImages.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-moss"><ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />No se extrajeron fotos de este PDF.</div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              <button onClick={() => { updateRow(picker, "image_url", ""); setPicker(null); }} className="aspect-square rounded-xl border border-dashed border-[#E3E7E2] grid place-items-center text-muted-moss hover:border-coral">
                <X className="w-5 h-5" />
              </button>
              {extractedImages.map((u, idx) => (
                <button key={idx} data-testid={`picker-image-${idx}`} onClick={() => { updateRow(picker, "image_url", u); setPicker(null); }} className={`aspect-square rounded-xl overflow-hidden border-2 ${rows?.[picker]?.image_url === u ? "border-coral" : "border-transparent hover:border-coral/50"}`}>
                  <img src={u} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
